import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faLink } from '@fortawesome/pro-light-svg-icons'

import {
  Button, Form, ModalContext, SidebarModal, SidebarNoticeBox,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useCampaignInspection, { useCampaignInspectionForm } from '@hooks/useCampaignInspection'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import CampaignSubHeader from '@components/CampaignSubHeader'

const MODAL_KEY = 'CreateOrEditCampaignInspectionModal'

type ModalCallbacks = {
  closeModal: () => void,
  createCampaignInspection: (payload: HandleCallbackActionParams) => void,
  updateCampaignInspection: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditCampaignInspectionModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, createCampaignInspection, updateCampaignInspection } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const campaignInspection = digObject(modalPayload, 'campaignInspection', {})

  const newRecord = !campaignInspection.id

  const {
    callbacks: {
      createCampaignInspection: createFn,
      updateCampaignInspection: updateFn,
    },
    campaign,
    campaignSyncedWithPortal,
    creating,
    updating,
  } = useCampaignInspection(campaignInspection)

  const {
    entityState,
    entityState: {
      end_time,
      inspection_date,
      start_time,
    },
    errors,
    handlers,
    saveEnabled,
  } = useCampaignInspectionForm(campaignInspection, { validateOn: 'change' })

  const createOrUpdateCampaignInspectionPayload = {
    callbacks: {
      action: newRecord ? createFn : updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Campaign Inspection ${newRecord ? 'Created' : 'Updated'}`,
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title={newRecord ? 'Create' : 'Edit'}
        titleSecondLine="Inspection"
      />

      {/*
       // @ts-expect-error TS2740 */}
      <CampaignSubHeader campaign={campaign} />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="Inspection Date" errorMessage={errors.inspection_date}>
            <input name="inspection_date" type="date" value={inspection_date} {...handlers} />
          </Form.Field>

          <Form.Row marginTop="large">
            <Form.Field label="Start Time" errorMessage={errors.start_time}>
              <input name="start_time" type="time" value={start_time} {...handlers} />
            </Form.Field>

            <Form.Field label="End Time" errorMessage={errors.end_time}>
              <input name="end_time" type="time" value={end_time} {...handlers} />
            </Form.Field>
          </Form.Row>
        </Form>

        {campaignSyncedWithPortal && (
          <SidebarNoticeBox boxProps={{ marginTop: 'large' }}>
            <SidebarNoticeBox.Title>
              <FontAwesomeIcon icon={faLink} /> Portal Syncing
            </SidebarNoticeBox.Title>

            <SidebarNoticeBox.Paragraph>
              This campaign has portal syncing turned on. Your inspection times might be overridden
              if changes are made in your CRM.
            </SidebarNoticeBox.Paragraph>
          </SidebarNoticeBox>
        )}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={
            newRecord
              ? () => createCampaignInspection(createOrUpdateCampaignInspectionPayload)
              : () => updateCampaignInspection(createOrUpdateCampaignInspectionPayload)
          }
          size="large"
        >
          {saveEnabled ? `${newRecord ? 'Create' : 'Update'} Inspection` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditCampaignInspectionModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
